import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { BRAND, CORE_DIVISIONS, TECHNICAL_SNAPSHOT, IMAGES, PROBLEMS } from "@/data/content";

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative min-h-[100vh] flex items-end overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img src={IMAGES.hero} alt="" className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/70 to-[#0A0A0A]/40" />
        </div>

        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-40 pb-20 w-full">
          <Reveal>
            <div className="overline mb-8 flex items-center gap-3">
              <span className="divider-rule"></span>
              <span>Designlayer — Est. studio of design, visualization & production systems</span>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="font-serif text-[11vw] md:text-[8.4vw] leading-[0.92] tracking-tighter text-[#EAEAEA]">
              High-end<br />
              visualization <span className="text-[#A3A3A3] italic font-light">&amp;</span><br />
              <span className="text-[#B58B5D] italic">cabinetry systems.</span>
            </h1>
          </Reveal>
          <Reveal delay={240}>
            <div className="mt-10 grid md:grid-cols-12 gap-8 items-end">
              <p className="md:col-span-7 text-lg md:text-xl text-[#A3A3A3] leading-relaxed max-w-2xl">
                Designlayer creates photorealistic architectural renderings, custom interior and
                cabinetry designs, Cabinet Vision systems, production drawings, CNC-ready workflows
                and manufacturing automation — for residential and commercial projects.
              </p>
              <div className="md:col-span-5 flex flex-wrap gap-4 md:justify-end">
                <Link to="/portfolio" data-testid="hero-cta-portfolio" className="dl-btn-primary">
                  View Our Work <ArrowUpRight size={14} />
                </Link>
                <Link to="/contact" data-testid="hero-cta-contact" className="dl-btn">
                  Discuss Your Project
                </Link>
              </div>
            </div>
          </Reveal>
        </div>

        {/* marquee */}
        <div className="absolute bottom-0 inset-x-0 border-t border-white/5 bg-[#0A0A0A]/70 backdrop-blur-md py-4 overflow-hidden">
          <div className="marquee-track text-[#A3A3A3] font-mono text-xs uppercase tracking-[0.3em]">
            {Array.from({ length: 2 }).map((_, i) => (
              <span key={i} className="flex items-center gap-16">
                <span>Design</span><span className="text-[#B58B5D]">●</span>
                <span>Visualize</span><span className="text-[#B58B5D]">●</span>
                <span>Engineer</span><span className="text-[#B58B5D]">●</span>
                <span>Manufacture</span><span className="text-[#B58B5D]">●</span>
                <span>Cabinet Vision</span><span className="text-[#B58B5D]">●</span>
                <span>UCS Automation</span><span className="text-[#B58B5D]">●</span>
                <span>CNC-Ready</span><span className="text-[#B58B5D]">●</span>
                <span>Photorealistic Rendering</span><span className="text-[#B58B5D]">●</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* INTRODUCTION */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-32" data-testid="home-intro">
        <div className="grid md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-4">
            <div className="overline mb-6">— 01 / Studio</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight">
              One company connecting <em className="text-[#B58B5D] not-italic">design</em>, <em className="text-[#B58B5D] not-italic">visualization</em> and <em className="text-[#B58B5D] not-italic">production</em>.
            </h2>
          </Reveal>
          <Reveal className="md:col-span-7 md:col-start-6 space-y-6 text-[#A3A3A3] text-lg leading-relaxed">
            <p>
              A successful project must be understood before it is built and controlled before it
              enters production. Designlayer connects these requirements through design expertise,
              photorealistic visualization, technical cabinetry knowledge and manufacturing systems.
            </p>
            <p>
              For design clients, we create accurate and persuasive images that communicate space,
              material, lighting and atmosphere. For cabinetry and millwork businesses, we develop
              systems that reduce repetitive drafting, standardize construction and move reliable
              information from approval through CNC production and installation.
            </p>
          </Reveal>
        </div>
      </section>

      {/* THREE DIVISIONS */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24" data-testid="home-divisions">
        <div className="grid md:grid-cols-3 gap-6">
          {CORE_DIVISIONS.map((d, i) => (
            <Reveal key={d.title} delay={i * 80}>
              <Link to={d.href} data-testid={`division-card-${i}`} className="dl-card group relative block h-full overflow-hidden">
                <div className="aspect-[4/5] overflow-hidden">
                  <img src={d.image} alt={d.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 opacity-90" />
                </div>
                <div className="p-7">
                  <div className="overline mb-3">{d.overline}</div>
                  <h3 className="font-serif text-2xl mb-3 text-[#EAEAEA] group-hover:text-[#B58B5D] transition-colors">{d.title}</h3>
                  <p className="text-sm text-[#A3A3A3] leading-relaxed">{d.body}</p>
                  <div className="mt-6 flex items-center gap-2 text-[#B58B5D] text-xs font-mono uppercase tracking-[0.18em]">
                    Explore <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* VALUE PROPOSITION */}
      <section className="border-y border-white/5 bg-[#0E0E0E] py-32" data-testid="home-value">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <div className="overline mb-6">— Value</div>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight">
              Why build or enter the same information <em className="text-[#B58B5D] not-italic">more than once?</em>
            </h2>
          </Reveal>
          <Reveal className="md:col-span-6 md:col-start-7 text-[#A3A3A3] text-lg leading-relaxed space-y-6">
            <p>
              A well-developed system can allow the same project information to support customer
              approvals, estimating, production drawings, bills of materials, hardware requirements,
              door ordering, CNC machining, assembly information, shipping documentation and
              installation drawings.
            </p>
            <p className="text-[#EAEAEA]">
              Designlayer is not focused only on drawing faster. The objective is to create more
              reliable information, clearer communication and a more controlled path from design
              intent to finished installation.
            </p>
          </Reveal>
        </div>
      </section>

      {/* CAPABILITIES SNAPSHOT */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-32" data-testid="home-capabilities">
        <Reveal>
          <div className="overline mb-4">— Capability snapshot</div>
          <h2 className="font-serif text-4xl md:text-5xl mb-14 max-w-3xl leading-tight">
            Ten things we engineer into every Designlayer system.
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-x-12">
          {TECHNICAL_SNAPSHOT.map((t, i) => (
            <Reveal key={t} delay={i * 30}>
              <div className="flex items-baseline gap-5 py-5 border-b border-white/5">
                <span className="num text-3xl text-[#B58B5D] w-12">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-[15px] text-[#EAEAEA]">{t}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PROBLEMS */}
      <section className="bg-[#0E0E0E] border-y border-white/5 py-32" data-testid="home-problems">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <div className="overline mb-6">— Problems Designlayer solves</div>
            <h2 className="font-serif text-4xl md:text-5xl max-w-3xl leading-tight mb-16">
              The gap between an attractive concept and a buildable cabinet — closed.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-16">
            <Reveal>
              <h3 className="font-serif text-2xl text-[#B58B5D] mb-6">Visualization & client communication</h3>
              <ul className="space-y-4">
                {PROBLEMS.visualization.map((p) => (
                  <li key={p} className="flex gap-4 text-[#A3A3A3] text-[15px]">
                    <span className="text-[#B58B5D]">—</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={120}>
              <h3 className="font-serif text-2xl text-[#B58B5D] mb-6">Cabinetry & manufacturing</h3>
              <ul className="space-y-4">
                {PROBLEMS.cabinetry.slice(0, 8).map((p) => (
                  <li key={p} className="flex gap-4 text-[#A3A3A3] text-[15px]">
                    <span className="text-[#B58B5D]">—</span>
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-32" data-testid="home-founder">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <Reveal className="md:col-span-5">
            <div className="aspect-[4/5] overflow-hidden border border-white/5">
              <img src={IMAGES.founder} alt="Founder" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <Reveal className="md:col-span-6 md:col-start-7" delay={120}>
            <div className="overline mb-6">— The practice</div>
            <h3 className="font-serif text-4xl md:text-5xl leading-tight mb-6">
              A studio practice connecting <em className="text-[#B58B5D] not-italic">design, visualization</em> and <em className="text-[#B58B5D] not-italic">production.</em>
              <br />
              <span className="text-[#A3A3A3] italic text-2xl font-light">
                Interior design &bull; Cabinet Vision specialism &bull; Production systems
              </span>
            </h3>
            <p className="text-[#A3A3A3] text-lg leading-relaxed">
              A practice built on connecting customer presentations, designer intent, estimating,
              engineering, purchasing, CNC operation, assembly and installation. Rather than treating
              design and production engineering as unrelated tasks, Designlayer develops systems in
              which information created during design remains useful throughout the project
              lifecycle.
            </p>
            <Link to="/about" data-testid="home-about-cta" className="dl-btn mt-10">
              Read the studio story
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/5 bg-gradient-to-b from-[#0A0A0A] to-[#050505] py-24" data-testid="home-cta">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="overline mb-6">— Next step</div>
            <h2 className="font-serif text-5xl md:text-6xl leading-tight tracking-tight">
              Build a better connection between <em className="text-[#B58B5D] not-italic">design and production.</em>
            </h2>
            <p className="text-[#A3A3A3] mt-6 max-w-2xl">
              Every company has different products, standards, hardware, machinery and reporting
              requirements. Designlayer develops systems and visuals around the way the project
              actually needs to be presented and built.
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end gap-4 flex-wrap">
            <Link to="/contact" data-testid="home-final-cta-contact" className="dl-btn-primary">
              Discuss Your Project <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
